import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import { useCart } from '../contexts/CartContext'
import MediaWithShimmer from '../components/MediaWithShimmer'

const Checkout = () => {
  const { language } = useLanguage()
  const { cart, getCartTotal, clearCart } = useCart()
  const navigate = useNavigate()

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    address: '',
    postalCode: '',
    paymentMethod: 'card',
    cardNumber: '',
    cardExpiry: '',
    cardCvv: ''
  })

  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')

  useEffect(() => {
    if (cart.length === 0) {
      navigate('/cart')
    }
  }, [])

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      })
    }
  }

  const validate = () => {
    const newErrors = {}

    if (!formData.firstName.trim()) newErrors.firstName = 'required'
    if (!formData.lastName.trim()) newErrors.lastName = 'required'
    if (!formData.email.trim()) newErrors.email = 'required'
    if (!formData.phone.trim()) newErrors.phone = 'required'
    if (!formData.country.trim()) newErrors.country = 'required'
    if (!formData.city.trim()) newErrors.city = 'required'
    if (!formData.address.trim()) newErrors.address = 'required'
    if (!formData.postalCode.trim()) newErrors.postalCode = 'required'

    if (formData.paymentMethod === 'card') {
      if (!formData.cardNumber.trim()) newErrors.cardNumber = 'required'
      if (!formData.cardExpiry.trim()) newErrors.cardExpiry = 'required'
      if (!formData.cardCvv.trim()) newErrors.cardCvv = 'required'
    }

    return newErrors
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitError('')

    const newErrors = validate()
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    try {
      setSubmitting(true)
      const endpoint = import.meta?.env?.VITE_CREATE_ORDER_ENDPOINT || '/api/orders/create'
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('auth_token') : null
      const headers = { 'Content-Type': 'application/json' }
      if (token) headers.Authorization = `Bearer ${token}`
      const resp = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          formData,
          cart,
          total: getCartTotal(),
          meta: { userAgent: navigator.userAgent }
        })
      })

      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) {
        throw new Error(data?.error || 'Failed to place order')
      }

      clearCart()
      if (data?.order_id) {
        navigate(`/order-success/${data.order_id}`)
      } else {
        navigate('/order-success')
      }
    } catch (err) {
      setSubmitError(err?.message || 'Unexpected error')
    } finally {
      setSubmitting(false)
    }
  }

  const formatPrice = (price) => {
    const formatted = new Intl.NumberFormat('ru-RU').format(price).replace(/\s/g, '\u00A0')
    return `${formatted}\u00A0€`
  }

  const getProductName = (product) => {
    return language === 'en' ? product.nameEn : language === 'es' ? product.nameEs : product.name
  }

  const translations = {
    title: { ru: 'Оформление заказа', en: 'Checkout', es: 'Finalizar Compra' },
    shippingInfo: { ru: 'Информация о доставке', en: 'Shipping Information', es: 'Información de Envío' },
    firstName: { ru: 'Имя', en: 'First Name', es: 'Nombre' },
    lastName: { ru: 'Фамилия', en: 'Last Name', es: 'Apellido' },
    email: { ru: 'Email', en: 'Email', es: 'Correo Electrónico' },
    phone: { ru: 'Телефон', en: 'Phone', es: 'Teléfono' },
    country: { ru: 'Страна', en: 'Country', es: 'País' },
    city: { ru: 'Город', en: 'City', es: 'Ciudad' },
    address: { ru: 'Адрес', en: 'Address', es: 'Dirección' },
    postalCode: { ru: 'Индекс', en: 'Postal Code', es: 'Código Postal' },
    paymentMethod: { ru: 'Способ оплаты', en: 'Payment Method', es: 'Método de Pago' },
    card: { ru: 'Банковская карта', en: 'Credit/Debit Card', es: 'Tarjeta de Crédito/Débito' },
    cash: { ru: 'Наличные при получении', en: 'Cash on Delivery', es: 'Pago Contra Reembolso' },
    cardNumber: { ru: 'Номер карты', en: 'Card Number', es: 'Número de Tarjeta' },
    cardExpiry: { ru: 'Срок действия (MM/YY)', en: 'Expiry Date (MM/YY)', es: 'Fecha de Vencimiento (MM/AA)' },
    cardCvv: { ru: 'CVV', en: 'CVV', es: 'CVV' },
    orderSummary: { ru: 'Ваш заказ', en: 'Order Summary', es: 'Resumen del Pedido' },
    subtotal: { ru: 'Промежуточный итог', en: 'Subtotal', es: 'Subtotal' },
    shipping: { ru: 'Доставка', en: 'Shipping', es: 'Envío' },
    shippingFree: { ru: 'Бесплатно', en: 'Free', es: 'Gratis' },
    total: { ru: 'Итого', en: 'Total', es: 'Total' },
    placeOrder: { ru: 'Оформить заказ', en: 'Place Order', es: 'Realizar Pedido' },
    required: { ru: 'Обязательное поле', en: 'Required field', es: 'Campo obligatorio' }
  }

  const t = translations

  if (cart.length === 0) {
    return null
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 pb-6">
        <h1 className="text-4xl font-light mb-8 playfair">{t.title[language]}</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Shipping Information */}
              <div className="bg-white p-6">
                <h2 className="text-2xl font-light mb-6 playfair">{t.shippingInfo[language]}</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 montserrat">
                      {t.firstName[language]} *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border ${errors.firstName ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:border-black montserrat`}
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-xs mt-1 montserrat">{t.required[language]}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 montserrat">
                      {t.lastName[language]} *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border ${errors.lastName ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:border-black montserrat`}
                    />
                    {errors.lastName && (
                      <p className="text-red-500 text-xs mt-1 montserrat">{t.required[language]}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 montserrat">
                      {t.email[language]} *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border ${errors.email ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:border-black montserrat`}
                    />
                    {errors.email && (
                      <p className="text-red-500 text-xs mt-1 montserrat">{t.required[language]}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 montserrat">
                      {t.phone[language]} *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:border-black montserrat`}
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-xs mt-1 montserrat">{t.required[language]}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 montserrat">
                      {t.country[language]} *
                    </label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border ${errors.country ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:border-black montserrat`}
                    />
                    {errors.country && (
                      <p className="text-red-500 text-xs mt-1 montserrat">{t.required[language]}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 montserrat">
                      {t.city[language]} *
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border ${errors.city ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:border-black montserrat`}
                    />
                    {errors.city && (
                      <p className="text-red-500 text-xs mt-1 montserrat">{t.required[language]}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2 montserrat">
                      {t.address[language]} *
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border ${errors.address ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:border-black montserrat`}
                    />
                    {errors.address && (
                      <p className="text-red-500 text-xs mt-1 montserrat">{t.required[language]}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 montserrat">
                      {t.postalCode[language]} *
                    </label>
                    <input
                      type="text"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border ${errors.postalCode ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:border-black montserrat`}
                    />
                    {errors.postalCode && (
                      <p className="text-red-500 text-xs mt-1 montserrat">{t.required[language]}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-white p-6">
                <h2 className="text-2xl font-light mb-6 playfair">{t.paymentMethod[language]}</h2>

                <div className="space-y-4">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="card"
                      checked={formData.paymentMethod === 'card'}
                      onChange={handleChange}
                      className="w-4 h-4"
                    />
                    <span className="montserrat">{t.card[language]}</span>
                  </label>

                  {formData.paymentMethod === 'card' && (
                    <div className="ml-7 space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2 montserrat">
                          {t.cardNumber[language]} *
                        </label>
                        <input
                          type="text"
                          name="cardNumber"
                          value={formData.cardNumber}
                          onChange={handleChange}
                          placeholder="1234 5678 9012 3456"
                          className={`w-full px-4 py-3 border ${errors.cardNumber ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:border-black montserrat`}
                        />
                        {errors.cardNumber && (
                          <p className="text-red-500 text-xs mt-1 montserrat">{t.required[language]}</p>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2 montserrat">
                            {t.cardExpiry[language]} *
                          </label>
                          <input
                            type="text"
                            name="cardExpiry"
                            value={formData.cardExpiry}
                            onChange={handleChange}
                            placeholder="MM/YY"
                            className={`w-full px-4 py-3 border ${errors.cardExpiry ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:border-black montserrat`}
                          />
                          {errors.cardExpiry && (
                            <p className="text-red-500 text-xs mt-1 montserrat">{t.required[language]}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2 montserrat">
                            {t.cardCvv[language]} *
                          </label>
                          <input
                            type="text"
                            name="cardCvv"
                            value={formData.cardCvv}
                            onChange={handleChange}
                            placeholder="123"
                            className={`w-full px-4 py-3 border ${errors.cardCvv ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:border-black montserrat`}
                          />
                          {errors.cardCvv && (
                            <p className="text-red-500 text-xs mt-1 montserrat">{t.required[language]}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="cash"
                      checked={formData.paymentMethod === 'cash'}
                      onChange={handleChange}
                      className="w-4 h-4"
                    />
                    <span className="montserrat">{t.cash[language]}</span>
                  </label>
                </div>
              </div>

              {submitError && (
                <div className="bg-red-50 text-red-600 text-sm px-4 py-3">
                  {submitError}
                </div>
              )}

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={submitting}
                  className={`px-6 py-3 border border-black text-black hover:bg-black hover:text-white transition-colors montserrat ${submitting ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  {submitting ? '...' : t.placeOrder[language]}
                </button>
              </div>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 sticky top-24">
              <h2 className="text-2xl font-light mb-6 playfair">{t.orderSummary[language]}</h2>

              <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
                {cart.map((item, index) => {
                  const itemImages = Array.isArray(item.images) && item.images.length > 0 
                    ? item.images 
                    : (item.image ? [item.image] : [])
                  
                  return (
                  <div key={`${item.id}-${item.size}-${item.color}-${index}`} className="flex gap-4 pb-4 border-b border-gray-100">
                    <div className="w-20 h-24 flex-shrink-0">
                      <MediaWithShimmer
                        src={itemImages[0] || ''}
                        alt={getProductName(item)}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium montserrat">{getProductName(item)}</p>
                      <p className="text-xs text-gray-600 montserrat">
                        {item.size && `${item.size}`} {item.color && `• ${item.color}`}
                      </p>
                      <p className="text-sm montserrat">
                        {item.quantity} × {formatPrice(item.price)}
                      </p>
                    </div>
                  </div>
                )})}
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between montserrat">
                  <span className="text-gray-600">{t.subtotal[language]}</span>
                  <span className="font-medium">{formatPrice(getCartTotal())}</span>
                </div>
                <div className="flex justify-between montserrat">
                  <span className="text-gray-600">{t.shipping[language]}</span>
                  <span className="font-medium">{t.shippingFree[language]}</span>
                </div>
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between text-lg montserrat">
                    <span>{t.total[language]}</span>
                    <span className="font-medium">{formatPrice(getCartTotal())}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Checkout
